import { create } from 'zustand';

import {
  hasGuestAgeConfirmationCookie,
  saveAgeConfirmation,
} from '@/features/age-verification/utils';

const SESSION_STORAGE_KEY = 'age-confirmed-in-session';

const getSessionConfirmation = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const setSessionConfirmation = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

const readLocalConfirmation = (): boolean => {
  return getSessionConfirmation() || hasGuestAgeConfirmationCookie();
};

interface AgeConfirmationStore {
  isOpen: boolean;
  pendingAction: (() => void) | null;
  isConfirming: boolean;
  hasLocalConfirmation: boolean;
  openDialog: (action: () => void) => void;
  closeDialog: () => void;
  confirmAge: (isAuthenticated: boolean, onSuccess?: () => void) => Promise<void>;
  declineAge: () => void;
  checkAndExecute: (
    action: () => void,
    isRating18: boolean,
    isAuthenticated: boolean,
    hasConfirmedAge: boolean,
  ) => void;
  hasConfirmedInSession: () => boolean;
  clearSessionConfirmation: () => void;
  syncLocalConfirmation: () => void;
}

export const useAgeConfirmationStore = create<AgeConfirmationStore>((set, get) => ({
  isOpen: false,
  pendingAction: null,
  isConfirming: false,
  hasLocalConfirmation: false,

  openDialog: (action) => {
    set({ isOpen: true, pendingAction: action });
  },

  closeDialog: () => {
    set({ isOpen: false, pendingAction: null });
  },

  confirmAge: async (isAuthenticated, onSuccess) => {
    set({ isConfirming: true });

    const success = await saveAgeConfirmation(isAuthenticated);

    if (success) {
      const { pendingAction } = get();
      setSessionConfirmation(true);
      set({
        isOpen: false,
        pendingAction: null,
        isConfirming: false,
        hasLocalConfirmation: true,
      });

      if (onSuccess) {
        onSuccess();
      }

      if (pendingAction) {
        pendingAction();
      }
    } else {
      set({ isConfirming: false });
    }
  },

  declineAge: () => {
    set({ isOpen: false, pendingAction: null });
  },

  checkAndExecute: (action, isRating18, isAuthenticated, hasConfirmedAge) => {
    const hasConfirmedInSession = getSessionConfirmation();
    const hasGuestCookie = !isAuthenticated && hasGuestAgeConfirmationCookie();

    if (isRating18 && !hasConfirmedAge && !hasConfirmedInSession && !hasGuestCookie) {
      get().openDialog(action);
    } else {
      action();
    }
  },

  hasConfirmedInSession: () => {
    return getSessionConfirmation();
  },

  clearSessionConfirmation: () => {
    setSessionConfirmation(false);
    set({ hasLocalConfirmation: hasGuestAgeConfirmationCookie() });
  },

  syncLocalConfirmation: () => {
    set({ hasLocalConfirmation: readLocalConfirmation() });
  },
}));
